import {
  getAgentVersionOptions,
  getPlayerVersionOptions,
} from "fleet/redux-store/ui-state/network-device-versions/selectors";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FormCheck from "react-bootstrap/FormCheck";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormLabel from "react-bootstrap/FormLabel";
import FormText from "react-bootstrap/FormText";
import Row from "react-bootstrap/Row";
import { Link, Redirect } from "react-router-dom";
import Select from "react-select";
import * as Network from "redux-store/networks/selectors";
import { AdPlatformSyncedAlert } from "ui/alert/ad-platform-synced-alert";
import { BackButton } from "ui/button/back";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { OperationScheduleForm } from "ui/operation-schedule/operation-schedule-form";
import { getErrorMessage } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

const DAYS = Object.freeze([
  "Disable",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Every day",
]);

class EditNetworkPage extends React.PureComponent {
  static get propTypes() {
    return {
      fetchNetworkDeviceVersions: PropTypes.func.isRequired,
      network: PropTypes.object,
      readonly: PropTypes.bool.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      updateNetworkDetails: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      ...this._getNetworkDetails(props.network),
      agentVersionOptions: [],
      playerVersionOptions: [],
      errorMessage: "",
      submitted: false,
      successMessage: "",
    };
    this.dayOptions = DAYS.map((label, value) => ({ value, label })).map(
      this._renderOptions,
    );
    this.hourOptions = this._getHourOptions(24).map(this._renderOptions);

    this._submit = this._submit.bind(this);
    this._fetchNetworkState = this._fetchNetworkState.bind(this);
    this._updateAgentVersion = this._updateAgentVersion.bind(this);
    this._updateDeviceUpdateDisabled =
      this._updateDeviceUpdateDisabled.bind(this);
    this._updateDeviceOperateHours = this._updateDeviceOperateHours.bind(this);
    this._updateDay = this._updateDay.bind(this);
    this._updateHour = this._updateHour.bind(this);
    this._updatePlayerVersion = this._updatePlayerVersion.bind(this);
    this._updateNetwork = this._updateNetwork.bind(this);
    this._updateNetworkName = this._updateNetworkName.bind(this);
    this._updateSandboxStatus = this._updateSandboxStatus.bind(this);
  }

  componentDidMount() {
    const { fetchNetworkDeviceVersions, network } = this.props;

    if (network) {
      fetchNetworkDeviceVersions(network.id).then((res) => {
        const { versions = {} } = res.data.data;
        const agentVersionOptions = getAgentVersionOptions(versions);
        const playerVersionOptions = getPlayerVersionOptions(versions);

        this.setState({
          agentVersionOptions,
          playerVersionOptions,
        });
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { network } = this.props;
    const { network: prevNetwork } = prevProps;

    if (network && prevNetwork && network.id !== prevNetwork.id) {
      this._fetchNetworkState(network);
    }
  }

  _getNetworkDetails(network) {
    const details = {
      agentVersion: null,
      day: 0,
      deviceOperateTime: "",
      deviceUpdateDisabled: true,
      hour: 0,
      networkName: "",
      playerVersion: null,
      sandboxStatus: true,
    };

    if (!network) {
      return details;
    }

    const {
      device_restart_time: drt,
      agent_version: agentVersion,
      player_version: playerVersion,
    } = network;

    if (agentVersion) {
      details.agentVersion = {
        value: agentVersion,
        label: agentVersion,
      };
    }

    if (playerVersion) {
      details.playerVersion = {
        value: playerVersion,
        label: playerVersion,
      };
    }
    details.deviceUpdateDisabled = network.device_update_disabled;
    details.deviceOperateTime = network.device_operate_time;
    details.networkName = network.name;
    details.sandboxStatus = network.is_sandbox;

    if (drt) {
      let time;
      if (drt.startsWith("*")) {
        time = moment(drt, "*-H");
        if (time.isValid()) {
          // 8 means every day in the UI.
          details.day = 8;
          details.hour = time.hour();
        }
      } else {
        time = moment(drt, "E-H");
        if (time.isValid()) {
          details.day = time.isoWeekday();
          details.hour = time.hour();
        }
      }
    }

    return details;
  }

  _fetchNetworkState(network) {
    const { fetchNetworkDeviceVersions } = this.props;
    const details = this._getNetworkDetails(network);

    return fetchNetworkDeviceVersions(network.id)
      .then((res) => {
        const { versions = {} } = res.data.data;
        const agentVersionOptions = getAgentVersionOptions(versions);
        const playerVersionOptions = getPlayerVersionOptions(versions);

        this.setState({
          agentVersionOptions,
          playerVersionOptions,
          errorMessage: "",
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState(details);
      });
  }

  _updateDeviceOperateHours(deviceOperateTime) {
    this.setState({ deviceOperateTime });
  }

  _updateDay(e) {
    this.setState({ day: e.currentTarget.value });
  }

  _updateHour(e) {
    this.setState({ hour: e.currentTarget.value });
  }

  _updateDeviceUpdateDisabled(e) {
    this.setState({ deviceUpdateDisabled: e.currentTarget.checked });
  }

  _updateAgentVersion(opt) {
    this.setState({ agentVersion: opt });
  }

  _updatePlayerVersion(opt) {
    this.setState({ playerVersion: opt });
  }

  _updateSandboxStatus(e) {
    this.setState({ sandboxStatus: e.currentTarget.checked });
  }

  _updateNetworkName(e) {
    this.setState({ networkName: e.currentTarget.value });
  }

  _submit() {
    this.setState({ submitted: true }, this._updateNetwork);
  }

  _updateNetwork() {
    const { network, updateNetworkDetails } = this.props;
    const {
      networkName,
      agentVersion,
      deviceOperateTime,
      deviceUpdateDisabled,
      day,
      hour,
      playerVersion,
      sandboxStatus,
    } = this.state;
    const playerVersionValue = playerVersion ? playerVersion.value : null;
    const agentVersionValue = agentVersion ? agentVersion.value : null;
    const networkDetails = {
      day,
      hour,
      networkName,
      sandboxStatus,
      agentVersion: agentVersionValue,
      playerVersion: playerVersionValue,
      disableRemoteUpdates: deviceUpdateDisabled,
      operateParams: deviceOperateTime,
    };

    const svcArgs = {
      networkId: network.id,
      networkDetails,
    };

    return updateNetworkDetails(svcArgs)
      .then(() => {
        this.setState({
          errorMessage: "",
          successMessage: "Network details updated.",
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({
          submitted: false,
        });
      });
  }

  _renderDeviceVersions() {
    const {
      agentVersion,
      agentVersionOptions,
      playerVersion,
      playerVersionOptions,
    } = this.state;

    return (
      <FormGroup controlId="device-versions">
        <FormLabel>Restrict Player Version</FormLabel>
        <Select
          name="player-versions"
          onChange={this._updatePlayerVersion}
          options={playerVersionOptions}
          value={playerVersion}
          isClearable
        />
        <FormText>
          You’ll only be able to upgrade or downgrade players under this network
          to the specified version.
        </FormText>
        <FormLabel>Restrict Agent Version</FormLabel>
        <Select
          name="agent-versions"
          onChange={this._updateAgentVersion}
          options={agentVersionOptions}
          value={agentVersion}
          isClearable
        />
        <FormText>
          You’ll only be able to upgrade agents under this network to the
          specified version.
        </FormText>
      </FormGroup>
    );
  }

  _renderNetworkName() {
    const systemManaged = Network.isSystemManaged(this.props.network);
    const { networkName } = this.state;

    return (
      <FormControl
        placeholder="Network Name"
        disabled={systemManaged}
        type="text"
        value={networkName}
        onChange={this._updateNetworkName}
      />
    );
  }

  _renderRestartSchedule() {
    const { day, hour } = this.state;

    return (
      <FormGroup controlId="restartSchedule">
        <Row>
          <Col xs={9}>
            {/*
             * React-Select has a bug that clicks through to
             * occluded components. So we use a native select.
             * And in 4 years, nobody fixed it.
             * https://github.com/JedWatson/react-select/issues/532
             */}
            <FormControl
              as="select"
              name="day-select"
              value={day}
              onChange={this._updateDay}
            >
              {this.dayOptions}
            </FormControl>
          </Col>
          <Col xs={3}>
            <FormControl
              as="select"
              name="hour-select"
              value={hour}
              onChange={this._updateHour}
            >
              {this.hourOptions}
            </FormControl>
          </Col>
        </Row>
        <FormText>
          Players belong to this network will auto restart at the selected hour
          and day every week. At restart time only the Cortex player will
          restart. The operating system will not get rebooted. The actual
          restart time may vary slightly from what you have selected.
        </FormText>
      </FormGroup>
    );
  }

  _renderSandboxCheckbox() {
    const { sandboxStatus } = this.state;

    const checkbox = (
      <FormCheck
        checked={sandboxStatus}
        label="This is a sandbox network"
        onChange={this._updateSandboxStatus}
        type="checkbox"
      />
    );

    return (
      <FormGroup>
        {checkbox}
        <FormText>
          Sandbox networks are for testing / staging devices. Devices under a
          sandbox network will not be included in some reports / stats.
        </FormText>
      </FormGroup>
    );
  }

  _renderDeviceUpdateCheckbox() {
    const { deviceUpdateDisabled } = this.state;

    const checkbox = (
      <FormCheck
        checked={deviceUpdateDisabled}
        label="Disable remote upgrades/downgrades"
        onChange={this._updateDeviceUpdateDisabled}
        type="checkbox"
      />
    );

    return (
      <FormGroup>
        <label className="control-label">Remote upgrades</label>
        {checkbox}
        <FormText>
          Checking this option will disable remote upgrades and downgrades for
          all devices under this network. You will need to handle device
          upgrades and downgrades manually.
        </FormText>
      </FormGroup>
    );
  }

  _getHourOptions(lastHour) {
    return Array.from(Array(lastHour), (el, value) => {
      return {
        value,
        label: `${String(value).padStart(2, "0")}:00`,
      };
    });
  }

  _renderOptions({ label, value }) {
    return (
      <option key={value} value={value}>
        {label}
      </option>
    );
  }

  _renderView() {
    const { network, readonly, storesLoaded } = this.props;
    const { deviceOperateTime, errorMessage, submitted, successMessage } =
      this.state;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!network) {
      return <Redirect to="/404" />;
    }

    return (
      <Row>
        <Col xs={12}>
          <Notification
            error={errorMessage}
            success={successMessage}
            successChildren={
              <Link to={`/networks/${network.id}`}>View Network</Link>
            }
          />
          <Row>
            <Col xs={6}>
              <label className="network-name">Network Name</label>
              {this._renderNetworkName()}
              <hr />
              <label className="control-label">Sandbox network</label>
              {this._renderSandboxCheckbox()}
              <hr />
              <label>Auto restart schedule</label>
              {this._renderRestartSchedule()}
            </Col>
            <Col xs={6}>
              {this._renderDeviceVersions()}
              <hr />
            </Col>
            <Col xs={6}>
              {this._renderDeviceUpdateCheckbox()}
              <hr />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FormGroup controlId="deviceOperateSchedule">
                <FormLabel>Display Operation Schedule</FormLabel>
                <FormText>
                  Players belong to this network will only operate between these
                  hours and the display will be turned off outside of these
                  hours.
                  <br />
                  <b>00:00 - 24:00 means, it operates at all times.</b>
                </FormText>
                <OperationScheduleForm
                  schedule={deviceOperateTime}
                  onChange={this._updateDeviceOperateHours}
                  readOnly={readonly}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <BackButton />
              <Button
                variant="primary"
                onClick={this._submit}
                disabled={submitted}
              >
                Update
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  render() {
    const { network } = this.props;
    const name = network ? network.name : "Network";
    const systemManaged = Network.isSystemManaged(network);
    const view = this._renderView();

    return (
      <Page title={`Edit ${name}`}>
        {systemManaged && <AdPlatformSyncedAlert objectName="network" />}
        <Box noHeader={true}>{view}</Box>
      </Page>
    );
  }
}

export { EditNetworkPage };
